/*
* Created on Jan 19, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import {createContext, useContext, useEffect, useState} from 'react';
import axios from "axios";
import {useRegion} from "./RegionContext";


const AuthSessionContext = createContext(null);
export const useAuthSession = () => useContext(AuthSessionContext)


const useAsyncEffect = (onSuccess, onError, checkOAuth=false) => {
    const {url} = useRegion()

    const doSignin = (state='') => {
        fetch(`/doSignIn${state}`).then(resp=>{
            resp.json().then(res=>{
                window.location.href = res.result.url
            })
        });
    };

    useEffect(()=>{
        let isMounted = true;
        const controller = new AbortController();
        const {signal} = controller;
        if (checkOAuth) {
            const data = {
                app_id: window._env_.REACT_APP_OAUTH_CLINET_ID
            }
            fetch(`${url.get_acc_url()}/oauth/login_status`, {
                signal: signal,
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: "include",
                body: JSON.stringify(data)
            }).then(resp=>resp.json()).then(resp=> {
                // app_not_authorized = live ACC session, no Org grant (post-signup hand-off):
                // one silent sign-in attempt per tab, then fall back to the landing page.
                if (resp.status === 'app_not_authorized' && !sessionStorage.getItem('oc_auto_signin_attempted')) {
                    sessionStorage.setItem('oc_auto_signin_attempted', '1');
                    sessionStorage.removeItem('access_token');
                    const returnTo = window.location.pathname.startsWith('/oauth/receive_auth')
                        ? '/'
                        : `${window.location.pathname}${window.location.search}`;
                    doSignin(`?${new URLSearchParams({state: returnTo}).toString()}`);
                    return;
                }
                if (["not_authorized", "app_not_authorized"].includes(resp.status)) {
                    sessionStorage.removeItem('access_token');
                    window.localStorage.removeItem('currentOrg');
                    onError({response: {data: {code: 'session_expired'}}});
                    return;
                }
                if (resp.status !== 'connected') {
                    const search = window.location.search;
                    doSignin(search);
                }
                else {
                    sessionStorage.removeItem('oc_auto_signin_attempted');
                    const access_token = resp.access_token;
                    if (isMounted) {
                        onSuccess(access_token);
                    }
                }
            }).catch(err=>{
                console.error(err)
            });
        }
        else {
            const access_token = sessionStorage?.access_token;
            if (access_token) {
                onSuccess(access_token);
            }
            else {
                onError({message: 'token_not_found'})
            }
        }

        return () => { controller.abort(); isMounted = false;}
    }, []);
}

export const AuthSessionProvider = ({children, updateLogin}) => {
    let access_token = sessionStorage.getItem('access_token');
    const [session, setSession] = useState(access_token || {});

    const useCheckSession = (isCheckOAuth) => {
        useAsyncEffect((resp) => {
            if (resp) {
                access_token = resp;
            }

            if (!access_token) {
                setSession({});
            }
            else {
                if (["not_authorized", "app_not_authorized"].includes(resp?.data?.status)) {
                    setSession(false);
                    updateLogin(false);
                } else {
                    sessionStorage.setItem('access_token', access_token);
                    setSession(true);
                    updateLogin(true);
                }
            }
        }, (error)=>{
            if (error?.response?.data?.code === "session_expired" || error?.message === 'token_not_found') {
                setSession(false);
            }
            updateLogin(false);
        }, isCheckOAuth, session);
    }

    return (
        <AuthSessionContext.Provider value={{session, useCheckSession}}>
            {children}
        </AuthSessionContext.Provider>
    )
}
